import { UserOutlined, EyeOutlined, DeleteOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Button, Space, Popover, Popconfirm, Tag, Input, Tooltip } from 'antd';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import config from '../../../config';
import { useSelector } from 'react-redux';
import canSeeFinance from '../../../utils/canSeeFinance';
import {
  NdoePage, NdoePageHeader, NdoeToolbar, NdoeActionButton,
  NdoeResponsiveTable, NdoeDataCard, NdoeImage,
} from '../../../components/ndoe';
import { estOfferte, libellePrix } from '../../../utils/prixOfferte';

const ListeDetailCommande = () => {
    const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const user = useSelector((state) => state.user?.currentUser);
    const isAdmin = user?.role === 'admin';
    const showFinance = canSeeFinance(user);

    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/commande/detail-commande`);
        setData(data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DOMAIN]);

    const handleDelete = async (id) => {
      try {
        await axios.delete(`${DOMAIN}/api/commande/detail-commande/${id}`);
        fetchData();   // refetch au lieu de recharger toute la page
      } catch (err) {
        console.log(err);
      }
    };

    const baseColumns = [
      { title: '#', dataIndex: 'id', key: 'id', render: (text, record, index) => index + 1, width: "3%" },
      { title: 'Id commande', dataIndex: 'id_commande', key: 'id_commande' },
      {
        title: 'image', dataIndex: 'img', key: 'image',
        render: (text, record) => (
          <NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />
        )
      },
      { title: 'Produit', dataIndex: 'id_varianteProduit', key: 'id_produit' },
      { title: 'Quantité', dataIndex: 'quantite', key: 'quantite' },
      {
        title: 'Prix', dataIndex: 'prix', key: 'prix', finance: true,
        sorter: (a, b) => a.prix - b.prix,
        sortDirections: ['descend', 'ascend'],
        // Une ligne à zéro est une paire OFFERTE, pas un prix manquant.
        render: (text, record) => (
          <Tooltip title={estOfferte(text) ? (record?.motif_gratuite || 'Paire offerte') : ''}>
            <Tag color={estOfferte(text) ? 'gold' : 'green'}>{libellePrix(text)}</Tag>
          </Tooltip>
        ),
      },
      {
        title: 'Date demande & heure', width: 176, dataIndex: 'date_demande', key: 'date_demande',
        render: (text) => <span>{format(new Date(text), 'dd-MM-yyyy HH:mm:ss')}</span>,
      },
      {
        title: 'Créé par', dataIndex: 'username', key: 'username',
        render: (text) => <Space><UserOutlined /><Tag color="blue">{text}</Tag></Space>,
      },
      {
        title: 'Action', key: 'action',
        render: (text, record) => (
          <Space size="middle">
            <Popover title="Voir la liste de cette commande" trigger="hover">
              <Button icon={<EyeOutlined />} style={{ color: 'blue' }} onClick={() => navigate(`/listeDetailView/${record.id_commande}`)} />
            </Popover>
            {isAdmin &&
            <Popover title="Supprimer" trigger="hover">
              <Popconfirm title="Êtes-vous sûr de vouloir supprimer?" onConfirm={() => handleDelete(record.id_detail)} okText="Oui" cancelText="Non">
                <Button icon={<DeleteOutlined />} style={{ color: 'red' }} />
              </Popconfirm>
            </Popover>}
          </Space>
        ),
      },
    ];

    const columns = baseColumns.filter((c) => !c.finance || showFinance);

    const filteredData = data?.filter((item) =>
      String(item.id_commande ?? '').toLowerCase().includes(searchValue.toLowerCase()) ||
      String(item.username ?? '').toLowerCase().includes(searchValue.toLowerCase()) ||
      String(item.id_varianteProduit ?? '').toLowerCase().includes(searchValue.toLowerCase())
    );

    const renderCard = (record) => {
      const metrics = [
        { value: record.quantite, label: 'qté' },
        { value: `#${record.id_varianteProduit}`, label: 'variante' },
      ];
      if (showFinance) {
        metrics.push({ value: libellePrix(record.prix), label: estOfferte(record.prix) ? (record.motif_gratuite || 'offerte') : 'prix' });
      }
      return (
        <NdoeDataCard
          media={<NdoeImage src={record.img} alt={record.nom_produit || 'Article'} size="lg" />}
          title={`Commande #${record.id_commande}`}
          subtitle={record.username ? `Créé par ${record.username}` : undefined}
          badge={record.date_demande ? (
            <span className="ndoe-badge ndoe-badge--neutral"><ClockCircleOutlined /> {format(new Date(record.date_demande), 'dd-MM-yyyy')}</span>
          ) : null}
          metrics={metrics}
          onClick={() => navigate(`/listeDetailView/${record.id_commande}`)}
          actions={[
            <NdoeActionButton key="o" size="sm" variant="ink" icon={<EyeOutlined />} onClick={() => navigate(`/listeDetailView/${record.id_commande}`)}>Ouvrir</NdoeActionButton>,
            isAdmin && (
              <Popconfirm key="d" title="Supprimer cet article ?" onConfirm={() => handleDelete(record.id_detail)} okText="Oui" cancelText="Non">
                <NdoeActionButton size="sm" variant="danger" icon={<DeleteOutlined />}>Suppr.</NdoeActionButton>
              </Popconfirm>
            ),
          ].filter(Boolean)}
        />
      );
    };

  return (
    <NdoePage>
      <NdoePageHeader eyebrow="Commerce" title="Détail des commandes" subtitle="Voir le détail ligne par ligne" />

      <NdoeToolbar
        search={
          <Input.Search
            type="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Rechercher (commande, variante, auteur)…"
            allowClear
          />
        }
      />

      <NdoeResponsiveTable
        data={filteredData}
        columns={columns}
        rowKey="id_detail"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 8 }}
        renderCard={renderCard}
        empty={{ icon: '📦', title: 'Aucun détail', text: 'Aucune ligne de commande.' }}
      />
    </NdoePage>
  )
}

export default ListeDetailCommande
